// PRODUCTION — flow editor page state and handlers.
//
// The canonical draft is the Redux `FlowEditorTemplateDocument` (single source
// of truth). Flat editor shapes (`steps`, `groupSettingsById`, `emptyGroupUiIds`,
// `flowName`) are DERIVED from it; every edit dispatches a `FlowEditorApplyOp`
// document op applied purely in the reducer. Only selection/transient UI state
// stays local.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  AdminCreateOnboardingTemplate,
  AdminSaveOnboardingTemplate,
  FlowEditorApplyOp,
  FlowEditorSetDraft,
} from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { ADMIN_ROUTES } from 'src/constants/routes'
import { useHasPermission } from 'src/hooks/useHasPermission'
import {
  buildLmsTrainingStepLabel,
  getDocusignPartnerTemplates,
  getInsurancePolicySelectOptions,
  mergeSelectOptionForCurrentValue,
  resolveDocusignTemplateSelectValue,
  resolveInsurancePolicyRowValue,
} from 'src/selectors/collectStepReference'
import { getInsurancePolicies } from 'src/selectors/insurancePolicy'
import { getFlowEditorDraft } from 'src/selectors/onboardingFlowEditor'
import type { BasicSelectOption } from 'src/components/shared/form/BasicSelect'
import type { CollectDataTemplateCreateConfigActionDto } from 'src/interfaces/collectDataTemplateUpdate'
import type {
  FormStepConfig,
  LmsTrainingStepConfig,
  OnboardingTemplate,
  SignContractStepConfig,
  StepConfig,
} from 'src/interfaces/onboardingTemplate'
import { CollectDataStepType } from 'src/constants/collect-data/enums'
import {
  FLOW_EDITOR_FEATURE_FLAGS,
  isFlowEditorStepDeletable,
  isFlowEditorStepEditable,
} from 'src/constants/flowEditorFeatureFlags'
import { useFlowEditorAddSteps } from '../hooks/useFlowEditorAddSteps'
import { useFlowEditorBootstrap } from '../hooks/useFlowEditorBootstrap'
import { useStepDragDrop } from '../hooks/useStepDragDrop'
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard'
import {
  mintNewFlowEditorConfigId,
  normalizeCollectDataTemplateConfigId,
  resolveStepGroupConfigId,
} from 'src/utils/collectDataTemplateEditor/configIds'
import {
  flowEditorGroupInsuranceSyncDisabled,
  getInsuranceSyncBlockedByExistingStepsMessage,
  shouldRecreateFlowEditorInsuranceGroupOnSync,
} from 'src/utils/collectDataTemplateEditor/insuranceSync'
import { flowEditorStepGroupDefaultLabel } from 'src/utils/collectDataTemplateEditor/sidebarGroups'
import { getEditorStepsFromTemplate } from 'src/utils/collectDataTemplateEditor/stepApiMapping'
import { buildActionsFromDocumentDiff } from 'src/utils/flowEditorDocument/buildActionsFromDiff'
import {
  documentToEditorSteps,
  documentToEmptyGroupUiIds,
} from 'src/utils/flowEditorDocument/toEditor'
import { documentToGroupSettings, templateToDocument } from 'src/utils/flowEditorDocument/fromApi'
import { insuranceCoverageDrafts } from 'src/utils/flowEditorDocument/draftFactory'
import type { FlowEditorDocumentOp } from 'src/utils/flowEditorDocument/types'
import { logger } from 'src/utils/logger'
import {
  computeUnsavedStepIds,
  stepConfigWithDelayedIfSupported,
} from '../utils/flowEditorStateUtils'
import type { FlowEditorContextValue } from './flowEditorContextTypes'

import {
  buildFlowEditorSidebarGroups,
  buildSelectedGroupPanelProps,
  computeFlowEditorIsDirty,
  computeFlowEditorSaveBlockedMessage,
} from '../utils/flowEditorDerivedUtils'

export function useFlowEditorContextComposer(): FlowEditorContextValue {
  const {
    collectStepRefsPrimed,
    customerId,
    error,
    isCreateMode,
    pageBlockingLoading,
    savedSnapshot,
    saving,
    template,
    templateId,
  } = useFlowEditorBootstrap()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const insurancePolicyOptionsBase = useSelector(getInsurancePolicySelectOptions)
  const insurancePoliciesFromApi = useSelector(getInsurancePolicies)
  const docusignPartnerTemplates = useSelector(getDocusignPartnerTemplates)

  // ── Source of truth: the Redux draft document ──────────────────────────────
  const draft = useSelector(getFlowEditorDraft)

  const applyOp = useCallback(
    (op: FlowEditorDocumentOp) => {
      dispatch(FlowEditorApplyOp(op))
    },
    [dispatch],
  )

  // ── Derived flat editor state (reads for the existing UI) ───────────────────
  const steps = useMemo(() => (draft ? documentToEditorSteps(draft) : []), [draft])
  const groupSettingsById = useMemo(() => (draft ? documentToGroupSettings(draft) : {}), [draft])
  const emptyGroupUiIds = useMemo(() => (draft ? documentToEmptyGroupUiIds(draft) : []), [draft])
  const flowName = draft?.name ?? ''

  // ── Local selection / transient UI state ────────────────────────────────────
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [selectedGroupConfigId, setSelectedGroupConfigId] = useState<string | null>(null)
  const [insuranceSyncStepsError, setInsuranceSyncStepsError] = useState<string | null>(null)

  // Tracks whether the editor is still on screen, so the async tail of a create save does not
  // navigate after the user has already left.
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  const moveStepWithinGroup = useCallback(
    (stepId: string, configLocalId: string, toIndex: number) => {
      applyOp({ kind: 'moveStep', localId: stepId, toConfigLocalId: configLocalId, toIndex })
    },
    [applyOp],
  )

  const { addStepsToGroup, closeAddStepModal, openAddStepModal, showAddModal } =
    useFlowEditorAddSteps({
      setSelectedGroupConfigId,
      setSelectedStepId,
      templateId,
    })

  // Reset selection when a new template loads or a save replaces the draft.
  useEffect(() => {
    setSelectedStepId(null)
    setSelectedGroupConfigId(null)
    setInsuranceSyncStepsError(null)
  }, [template])

  // Resolve DocuSign template ids on sign-contract steps once reference data primes.
  useEffect(() => {
    if (!collectStepRefsPrimed || docusignPartnerTemplates.length === 0 || !draft) {
      return
    }

    for (const step of Object.values(draft.stepsByLocalId)) {
      if (step.type !== CollectDataStepType.SignContract) {
        continue
      }

      const signContractConfig = step.config as SignContractStepConfig
      const resolved = resolveDocusignTemplateSelectValue(
        docusignPartnerTemplates,
        signContractConfig.docusignTemplateId,
      )

      if (resolved === signContractConfig.docusignTemplateId) {
        continue
      }

      applyOp({
        kind: 'setStepConfig',
        localId: step.localId,
        config: { ...signContractConfig, docusignTemplateId: resolved },
      })
    }
  }, [applyOp, collectStepRefsPrimed, docusignPartnerTemplates, draft])

  const savedSnapshotEditorSteps = useMemo(
    () => (savedSnapshot ? getEditorStepsFromTemplate(savedSnapshot) : []),
    [savedSnapshot],
  )

  const unsavedStepIds = useMemo(() => {
    if (!savedSnapshot) {
      return new Set<string>()
    }

    return computeUnsavedStepIds(steps, savedSnapshotEditorSteps)
  }, [savedSnapshot, steps, savedSnapshotEditorSteps])

  // ── Dirty tracking ──────────────────────────────────────────────────────────
  const isDirty = useMemo(
    () =>
      computeFlowEditorIsDirty({
        emptyGroupUiIds,
        flowName,
        groupSettingsById,
        savedSnapshot,
        savedSnapshotEditorSteps,
        steps,
      }),
    [emptyGroupUiIds, flowName, groupSettingsById, steps, savedSnapshot, savedSnapshotEditorSteps],
  )

  // Unsaved changes guard (beforeunload + navigation block). Only for someone who can save: a
  // view-only user (no `collect_data_templates_manage`) may explore and rearrange the draft, but
  // blocking them over changes they can never keep would trap them on the page.
  const canSaveTemplate = useHasPermission(InternalPermission.CollectDataTemplatesManage)
  const { showUnsavedModal, confirmLeave, cancelLeave, dropGuardHistoryEntries, guardNavigate } =
    useUnsavedChangesGuard(isDirty && canSaveTemplate)

  // Drag and drop
  const {
    draggedStepConfigId,
    draggedStepId,
    dropTarget,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    isDropTarget,
  } = useStepDragDrop({
    steps,
    template,
    moveStepWithinGroup,
  })

  // ── Derived state ─────────────────────────────────────────────────────────
  const selectedStep = useMemo(
    () => steps.find((s) => s.id === selectedStepId) ?? null,
    [steps, selectedStepId],
  )
  const selectedStepGroupOrder = useMemo(() => {
    if (!template || !selectedStep || selectedStepId === null) {
      return null
    }

    const groupConfigId = resolveStepGroupConfigId(selectedStep, template)
    const groupSteps = steps.filter(
      (step) => resolveStepGroupConfigId(step, template) === groupConfigId,
    )
    const gi = groupSteps.findIndex((s) => s.id === selectedStepId)

    if (gi < 0) {
      return null
    }

    return { indexInGroup: gi, totalInGroup: groupSteps.length }
  }, [selectedStep, selectedStepId, steps, template])

  const flowEditorSidebarGroups = useMemo(() => {
    if (!template) {
      return []
    }

    return buildFlowEditorSidebarGroups({
      emptyGroupUiIds,
      groupSettingsById,
      steps,
      template,
    })
  }, [emptyGroupUiIds, groupSettingsById, steps, template])

  const saveBlockedMessage = useMemo(
    () =>
      computeFlowEditorSaveBlockedMessage({
        draft,
        emptyGroupUiIds,
        flowName,
        isCreateMode,
        steps,
        template,
      }),
    [draft, emptyGroupUiIds, flowName, isCreateMode, steps, template],
  )

  const selectedGroupPanelProps = useMemo(
    () =>
      buildSelectedGroupPanelProps({
        flowEditorSidebarGroups,
        groupSettingsById,
        selectedGroupConfigId,
        steps,
        template,
      }),
    [flowEditorSidebarGroups, groupSettingsById, selectedGroupConfigId, steps, template],
  )

  useEffect(() => {
    setInsuranceSyncStepsError(null)
  }, [selectedGroupConfigId, selectedGroupPanelProps?.insurancePolicyId])

  const groupInsurancePolicySelectOptions = useMemo((): BasicSelectOption[] => {
    const id = selectedGroupPanelProps?.insurancePolicyId

    return mergeSelectOptionForCurrentValue(insurancePolicyOptionsBase, id ?? undefined, 'Policy')
  }, [insurancePolicyOptionsBase, selectedGroupPanelProps?.insurancePolicyId])

  const selectedGroupInsuranceSyncDisabled = useMemo(() => {
    if (!template || selectedGroupConfigId === null) {
      return false
    }

    return flowEditorGroupInsuranceSyncDisabled(steps, template, selectedGroupConfigId)
  }, [selectedGroupConfigId, steps, template])

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleMoveStep = useCallback(
    (direction: 'up' | 'down') => {
      if (!FLOW_EDITOR_FEATURE_FLAGS.allowReorderSteps || !draft || selectedStepId === null) {
        return
      }

      const step = draft.stepsByLocalId[selectedStepId]

      if (!step) {
        return
      }

      const group = draft.configsByLocalId[step.configLocalId]

      if (!group) {
        return
      }

      const indexInGroup = group.stepLocalIds.indexOf(selectedStepId)

      if (indexInGroup < 0) {
        return
      }

      const toIndex = direction === 'up' ? indexInGroup - 1 : indexInGroup + 1

      if (toIndex < 0 || toIndex > group.stepLocalIds.length - 1) {
        return
      }

      applyOp({
        kind: 'moveStep',
        localId: selectedStepId,
        toConfigLocalId: step.configLocalId,
        toIndex,
      })
    },
    [applyOp, draft, selectedStepId],
  )

  const handleRemoveStep = useCallback(
    (stepId: string) => {
      if (!FLOW_EDITOR_FEATURE_FLAGS.allowDeleteSteps) {
        return
      }

      const target = steps.find((s) => s.id === stepId)

      if (!target || !isFlowEditorStepDeletable(target)) {
        return
      }

      applyOp({ kind: 'removeStep', localId: stepId })

      if (selectedStepId === stepId) {
        setSelectedStepId(null)
      }

      setPendingDeleteId(null)
    },
    [applyOp, selectedStepId, steps],
  )

  const handleAddEmptyStepGroup = useCallback(() => {
    if (!template) {
      return
    }

    const id = mintNewFlowEditorConfigId()
    const label = flowEditorStepGroupDefaultLabel(template, steps, [...emptyGroupUiIds, id], id)

    applyOp({ kind: 'addGroup', localId: id, label, delayed: false })
  }, [applyOp, emptyGroupUiIds, steps, template])

  const handleRemoveEmptyStepGroup = useCallback(
    (configId: string) => {
      applyOp({ kind: 'removeGroup', localId: configId })
      setSelectedGroupConfigId((prev) => (prev === configId ? null : prev))
    },
    [applyOp],
  )

  const handleSelectGroup = useCallback((configId: string) => {
    setSelectedStepId(null)
    setSelectedGroupConfigId(configId)
  }, [])

  const handleGroupLabelChange = useCallback(
    (configId: string, label: string) => {
      applyOp({ kind: 'setGroupSettings', localId: configId, patch: { label } })
    },
    [applyOp],
  )

  const handleGroupDelayedChange = useCallback(
    (configId: string, delayed: boolean) => {
      applyOp({ kind: 'setGroupSettings', localId: configId, patch: { delayed } })

      if (!draft) {
        return
      }

      const group = draft.configsByLocalId[configId]

      if (!group) {
        return
      }

      for (const stepLocalId of group.stepLocalIds) {
        const step = draft.stepsByLocalId[stepLocalId]

        if (!step) {
          continue
        }

        applyOp({
          kind: 'setStepConfig',
          localId: stepLocalId,
          config: stepConfigWithDelayedIfSupported(step.config, delayed),
        })
      }
    },
    [applyOp, draft],
  )

  const handleMoveStepGroup = useCallback(
    (configId: string, direction: 'up' | 'down') => {
      if (!FLOW_EDITOR_FEATURE_FLAGS.allowReorderSteps || !draft) {
        return
      }

      const index = draft.configLocalIds.indexOf(configId)

      if (index < 0) {
        return
      }

      const toIndex = direction === 'up' ? index - 1 : index + 1

      if (toIndex < 0 || toIndex > draft.configLocalIds.length - 1) {
        return
      }

      applyOp({ kind: 'reorderGroup', localId: configId, toIndex })
    },
    [applyOp, draft],
  )

  const handleGroupInsurancePolicyChange = useCallback(
    (configId: string, policyId: string | null) => {
      applyOp({
        kind: 'setGroupSettings',
        localId: configId,
        patch: { insurancePolicyId: policyId ?? undefined },
      })
    },
    [applyOp],
  )

  const handleSyncInsurancePolicySteps = useCallback(() => {
    if (!template || !draft || selectedGroupConfigId === null || selectedGroupPanelProps === null) {
      return
    }

    if (flowEditorGroupInsuranceSyncDisabled(steps, template, selectedGroupConfigId)) {
      return
    }

    const policyIdRaw = selectedGroupPanelProps.insurancePolicyId?.trim() ?? ''
    const hasPolicy = policyIdRaw !== ''
    const policy = hasPolicy
      ? insurancePoliciesFromApi.find(
          (policyRow) => resolveInsurancePolicyRowValue(policyRow) === policyIdRaw,
        )
      : undefined
    const products = policy?.insurancePoliciesProducts ?? []
    const productsForSync = products.map((product) => {
      return { id: product.id, insuranceProductId: product.insuranceProductId }
    })

    if (hasPolicy) {
      const blocked = getInsuranceSyncBlockedByExistingStepsMessage(
        steps,
        template,
        selectedGroupConfigId,
        productsForSync,
      )

      if (blocked) {
        setInsuranceSyncStepsError(blocked)

        return
      }
    }

    setInsuranceSyncStepsError(null)

    const policyName = policy?.name?.trim() ?? selectedGroupPanelProps.label.trim() ?? ''
    const coverageParams = {
      policyRowId: hasPolicy ? policyIdRaw : null,
      products: hasPolicy ? productsForSync : [],
      groupDelayed: selectedGroupPanelProps.delayed,
      policyName,
    }

    const recreateGroup = shouldRecreateFlowEditorInsuranceGroupOnSync(
      steps,
      template,
      selectedGroupConfigId,
      hasPolicy ? policyIdRaw : null,
    )

    if (recreateGroup) {
      const newConfigId = mintNewFlowEditorConfigId()
      const freshLabel = policyName !== '' ? policyName : 'Step group'
      const coverageSteps = insuranceCoverageDrafts(newConfigId, coverageParams)

      applyOp({
        kind: 'recreateGroupWithSteps',
        oldLocalId: selectedGroupConfigId,
        newLocalId: newConfigId,
        label: freshLabel,
        delayed: selectedGroupPanelProps.delayed,
        insurancePolicyId: hasPolicy ? policyIdRaw : undefined,
        steps: coverageSteps,
      })
      setSelectedGroupConfigId(newConfigId)
      setSelectedStepId(null)

      return
    }

    const coverageSteps = insuranceCoverageDrafts(selectedGroupConfigId, coverageParams)

    applyOp({
      kind: 'replaceGroupSteps',
      configLocalId: selectedGroupConfigId,
      steps: coverageSteps,
    })

    if (hasPolicy) {
      const existingLabel = groupSettingsById[selectedGroupConfigId]?.label?.trim() ?? ''
      let nextLabel = 'Step group'

      if (policyName !== '') {
        nextLabel = policyName
      } else if (existingLabel !== '') {
        nextLabel = existingLabel
      }

      applyOp({
        kind: 'setGroupSettings',
        localId: selectedGroupConfigId,
        patch: { label: nextLabel, insurancePolicyId: policyIdRaw },
      })
    }

    setSelectedStepId(null)
  }, [
    applyOp,
    draft,
    groupSettingsById,
    insurancePoliciesFromApi,
    selectedGroupConfigId,
    selectedGroupPanelProps,
    steps,
    template,
  ])

  const handleRemoveGroup = useCallback(
    (configId: string) => {
      const isEmpty = emptyGroupUiIds.some(
        (e) => (normalizeCollectDataTemplateConfigId(e) || e) === configId,
      )

      if (isEmpty) {
        handleRemoveEmptyStepGroup(configId)

        return
      }

      if (!draft || !FLOW_EDITOR_FEATURE_FLAGS.allowDeleteSteps) {
        return
      }

      const group = draft.configsByLocalId[configId]

      if (!group || group.stepLocalIds.length === 0) {
        return
      }

      const removedStepIds = new Set(group.stepLocalIds)

      applyOp({ kind: 'removeGroup', localId: configId })
      setSelectedGroupConfigId((prev) => (prev === configId ? null : prev))
      setSelectedStepId((prev) => (prev !== null && removedStepIds.has(prev) ? null : prev))
    },
    [applyOp, draft, emptyGroupUiIds, handleRemoveEmptyStepGroup],
  )

  const handleConfigChange = useCallback(
    (config: StepConfig) => {
      if (!selectedStepId || !draft) {
        return
      }

      const step = draft.stepsByLocalId[selectedStepId]
      const editorStep = steps.find((s) => s.id === selectedStepId)

      if (!step || !editorStep || !isFlowEditorStepEditable(editorStep)) {
        return
      }

      applyOp({ kind: 'setStepConfig', localId: selectedStepId, config })

      if (step.type === CollectDataStepType.Form) {
        applyOp({
          kind: 'setStepLabel',
          localId: selectedStepId,
          label: (config as FormStepConfig).label,
        })
      } else if (step.type === CollectDataStepType.LMSTraining) {
        applyOp({
          kind: 'setStepLabel',
          localId: selectedStepId,
          label: buildLmsTrainingStepLabel((config as LmsTrainingStepConfig).lmsTrainingId),
        })
      }
    },
    [applyOp, draft, selectedStepId, steps],
  )

  const handleRetroactiveCollectRequiredChange = useCallback(
    (retroactiveCollectRequired: boolean) => {
      if (!selectedStepId) {
        return
      }

      const editorStep = steps.find((s) => s.id === selectedStepId)

      if (!editorStep || !isFlowEditorStepEditable(editorStep)) {
        return
      }

      applyOp({
        kind: 'setStepScalars',
        localId: selectedStepId,
        patch: { retroactiveCollectRequired },
      })
    },
    [applyOp, selectedStepId, steps],
  )

  const handleAdvancedStepRowFieldChange = useCallback(
    (
      field:
        | 'ignoreMonitoring'
        | 'recollectSameTypeSteps'
        | 'skipRetroactiveCollectIfAnySameTypeCompleted',
      value: boolean,
    ) => {
      if (!selectedStepId) {
        return
      }

      const editorStep = steps.find((step) => step.id === selectedStepId)

      if (!editorStep || !isFlowEditorStepEditable(editorStep)) {
        return
      }

      applyOp({ kind: 'setStepScalars', localId: selectedStepId, patch: { [field]: value } })
    },
    [applyOp, selectedStepId, steps],
  )

  const handleCollectDataTemplateStepLabelChange = useCallback(
    (label: string) => {
      if (!selectedStepId || !draft) {
        return
      }

      const step = draft.stepsByLocalId[selectedStepId]
      const editorStep = steps.find((s) => s.id === selectedStepId)

      if (!step || !editorStep) {
        return
      }

      if (step.type === CollectDataStepType.Form) {
        if (!isFlowEditorStepEditable(editorStep)) {
          return
        }

        applyOp({ kind: 'setStepLabel', localId: selectedStepId, label })
        applyOp({
          kind: 'setStepConfig',
          localId: selectedStepId,
          config: { ...(step.config as FormStepConfig), label },
        })

        return
      }

      if (step.type !== CollectDataStepType.SignContract) {
        return
      }

      if (!isFlowEditorStepEditable(editorStep)) {
        return
      }

      applyOp({ kind: 'setStepLabel', localId: selectedStepId, label })
    },
    [applyOp, draft, selectedStepId, steps],
  )

  const handleRevert = useCallback(() => {
    if (!savedSnapshot) {
      return
    }

    dispatch(FlowEditorSetDraft(templateToDocument(savedSnapshot)))
    setSelectedStepId(null)
    setSelectedGroupConfigId(null)
    setInsuranceSyncStepsError(null)
    closeAddStepModal()
  }, [closeAddStepModal, dispatch, savedSnapshot])

  const handleSave = useCallback(async () => {
    if (!isDirty || !template || !savedSnapshot || !draft) {
      return
    }

    if (saveBlockedMessage) {
      logger.warn('Save blocked: flow editor validation', {
        templateId,
        reason: saveBlockedMessage,
      })

      return
    }

    const baselineDoc = templateToDocument(savedSnapshot)
    const actions = buildActionsFromDocumentDiff(baselineDoc, draft)

    if (actions.length === 0) {
      // Only client-only empty placeholder groups changed — drop them.
      if (emptyGroupUiIds.length > 0) {
        emptyGroupUiIds.forEach((id) => applyOp({ kind: 'removeGroup', localId: id }))

        return
      }

      logger.warn('Save skipped: no update actions to send', { templateId })

      return
    }

    try {
      if (isCreateMode) {
        const created = (await createAsyncAction(
          dispatch,
          AdminCreateOnboardingTemplate.request({
            body: {
              partnerId: customerId,
              name: flowName.trim(),
              actions: actions as CollectDataTemplateCreateConfigActionDto[],
            },
          }),
        )) as OnboardingTemplate

        if (created?.id) {
          setSelectedStepId(null)
          setSelectedGroupConfigId(null)
          // The dirty guard pushed duplicate `/new` entries while the draft was being filled in.
          // Unwind them first, so the `replace` below overwrites the `/new` entry the user
          // arrived on instead of a duplicate — otherwise Back would return to a blank create
          // editor (both routes render the same element, so it would not even remount).
          await dropGuardHistoryEntries()

          // The draft went clean the moment the create succeeded, so during the unwind above the
          // shell's Back button navigates straight through instead of being blocked by the unsaved
          // guard — and creating no longer opens a modal that would have covered this window. If
          // the editor is gone by the time we get here, there is nothing left to navigate.
          if (!isMountedRef.current) {
            return
          }

          // Continue in normal edit mode against the real id, so an immediate second save issues
          // an update rather than a second create. The draft already matches the response, so the
          // unsaved-changes guard does not fire on this navigation.
          navigate(
            {
              pathname: ADMIN_ROUTES.FLOW_EDITOR_URL.replace(':customerId', customerId).replace(
                ':templateId',
                created.id,
              ),
              search: window.location.search,
            },
            { replace: true },
          )
        }

        setJustSaved(true)
        setTimeout(() => setJustSaved(false), 2000)

        return
      }

      // The template NAME is deliberately not handled here — it is not expressible as an action
      // and is saved on its own from the rename modal (EditTemplateTitleModal).
      const savedTemplate = (await createAsyncAction(
        dispatch,
        AdminSaveOnboardingTemplate.request({
          templateId,
          body: { actions },
        }),
      )) as OnboardingTemplate

      if (savedTemplate) {
        setSelectedStepId(null)
        setSelectedGroupConfigId(null)
      }

      setJustSaved(true)
      setTimeout(() => setJustSaved(false), 2000)
    } catch {
      // Error handled by reducer
    }
  }, [
    applyOp,
    customerId,
    dispatch,
    draft,
    dropGuardHistoryEntries,
    emptyGroupUiIds,
    flowName,
    isCreateMode,
    isDirty,
    navigate,
    saveBlockedMessage,
    savedSnapshot,
    template,
    templateId,
  ])

  return {
    addStepsToGroup,
    cancelLeave,
    closeAddStepModal,
    confirmLeave,
    draggedStepConfigId,
    draggedStepId,
    dropTarget,
    error,
    flowEditorSidebarGroups,
    flowName,
    groupInsurancePolicySelectOptions,
    guardNavigate,
    handleAddEmptyStepGroup,
    handleCollectDataTemplateStepLabelChange,
    handleConfigChange,
    handleDragEnd,
    handleDragOver,
    handleDragStart,
    handleDrop,
    isDropTarget,
    handleGroupDelayedChange,
    handleGroupInsurancePolicyChange,
    handleGroupLabelChange,
    handleMoveStep,
    handleMoveStepGroup,
    handleRemoveEmptyStepGroup,
    handleRemoveGroup,
    handleRemoveStep,
    handleRetroactiveCollectRequiredChange,
    handleAdvancedStepRowFieldChange,
    handleRevert,
    handleSave,
    handleSelectGroup,
    handleSyncInsurancePolicySteps,
    insuranceSyncStepsError,
    isCreateMode,
    isDirty,
    justSaved,
    openAddStepModal,
    pageBlockingLoading,
    pendingDeleteId,
    saveBlockedMessage,
    saving,
    selectedGroupConfigId,
    selectedGroupInsuranceSyncDisabled,
    selectedGroupPanelProps,
    selectedStep,
    selectedStepGroupOrder,
    selectedStepId,
    setPendingDeleteId,
    setSelectedGroupConfigId,
    setSelectedStepId,
    showAddModal,
    showUnsavedModal,
    steps,
    template,
    unsavedStepIds,
  }
}
